import { FC } from 'react';
import { makeStaticUrl } from '@doda/common/src/logics/staticUrl';
import { DODA_SITE_URL } from '@doda/common/src/constants/urls';
import {
  RequirementsRequestCode,
  WorkplaceEnvironmentRequestCode,
  HolidayAndWayOfWorkingRequestCode,
  EmployeeAverageAgeRequestCode,
  EmploymentStatusRequestCode,
  WelfareBenefitsRequestCode,
  PositionsRequestCode,
  LanguageSkillsRequestCode,
} from '@doda/api-client/aspida/@types';
import { generateOpValues } from '@doda/common/src/logics/JobContent/JobTrend/generateOpValues';
import {
  ANALYTICS_OP_NAME,
  ANALYTICS_HA_NAME,
  ANALYTICS_ES_NAME,
  LABEL_FLAG,
  CURSOR_IMG_LINK,
  CLOSE_IMG_LINK,
} from '@doda/common/src/constants/seo/jobTrendConstants';
import { masterSelector } from '@doda/common/src/redux/features/master';
import { useAppSelector, useAppDispatch } from 'redux/hooks';
import { createOpParams } from '@doda/common/src/logics/JobContent/apiRequest/createOpParams';
import { formatDate } from '@doda/common/src/logics/JobContent/createFormatDate';
import { createPreBtn } from '@doda/common/src/logics/JobContent/createPreBtn';
import {
  initialDisplayFlagIncomeSelector,
  jobRecommendListForTrendIncomeEntitySelector,
  jobRecommendListForTrendIncomeLoadingSelector,
} from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendIncome';
import {
  initialDisplayFlagRequirementsSelector,
  jobRecommendListForTrendRequirementsEntitySelector,
  jobRecommendListForTrendRequirementsLoadingSelector,
} from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendRequirements';
import {
  initialDisplayFlagWorkplaceEnvironmentSelector,
  jobRecommendListForTrendWorkplaceEnvironmentEntitySelector,
  jobRecommendListForTrendWorkplaceEnvironmentLoadingSelector,
} from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendWorkplaceEnvironment1';
import {
  initialDisplayFlagHolidayAndWayOfWorkingSelector,
  jobRecommendListForTrendHolidayAndWayOfWorkingEntitySelector,
  jobRecommendListForTrendHolidayAndWayOfWorkingLoadingSelector,
} from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendHolidayAndWayOfWorking';
import {
  initialDisplayFlagEmployeeAverageAgeSelector,
  jobRecommendListForTrendEmployeeAverageAgeEntitySelector,
  jobRecommendListForTrendEmployeeAverageAgeLoadingSelector,
} from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendEmployeeAverageAge';
import {
  initialDisplayFlagEmploymentStatusSelector,
  jobRecommendListForTrendEmploymentStatusEntitySelector,
  jobRecommendListForTrendEmploymentStatusLoadingSelector,
} from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendEmploymentStatus';
import {
  initialDisplayFlagWelfareBenefitsSelector,
  jobRecommendListForTrendWelfareBenefitsEntitySelector,
  jobRecommendListForTrendWelfareBenefitsLoadingSelector,
} from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendWelfareBenefits';
import {
  initialDisplayFlagLanguageAndPositionsSelector,
  jobRecommendListForTrendLanguageAndPositionsEntitySelector,
  jobRecommendListForTrendLanguageAndPositionsLoadingSelector,
} from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendLanguageAndPositions';
import {
  initialDisplayFlagEstablishSelector,
  jobRecommendListForTrendEstablishEntitySelector,
  jobRecommendListForTrendEstablishLoadingSelector,
} from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendListForTrendEstablish';
import { ArrowRight, Snackbar } from '@doda/io-react/common';
import { measureJobSearchListLink } from '@doda/common/src/logics/JobContent/JobTrend/analytics';
import { AppQueryParams } from '@doda/common/src/constants/queryParams';
import { JobRecommendOffsetState } from '@doda/common/src/redux/features/JobContent/JobTrend/jobRecommendOffset';
import { JobTrendRecommendListState } from '@doda/common/src/redux/features/JobContent/JobTrend/jobTrendRecommendList';
import { BargraphForJobTrend } from '@doda/common/src/components/JobContent/JobTrend/BargraphForJobTrend';
import { convertQuery } from '@doda/common/src/logics/JobContent/analytics';
import { useBargraphSelected } from '@doda/common/src/hooks/JobContent/JobTrend/useBargraphSelected';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { FeatureFlagSettings } from '@doda/common/src/constants/featureFlag';
import { BargraphLeadText } from '../BargraphLeadText';
import { JobRecommend } from '../JobRecommend';
import { InitialJobRecommend } from '../InitialJobRecommend';
import styles from './index.module.scss';

/**
 * JobCountBargraphAreaByCategoryのProps
 * @param targetName ページの一軸名
 * @param categoryConstants カテゴリ名
 * @param categoryFlg こだわりフラグ名
 * @param rankingList 棒グラフ用配列(requirementsなどで指定したものを渡す)
 * @param bargraphSelected クリックされた項目名と求人数をState管理するsetと値が入っている
 * @param queryParams クエリパラメータ(例：st: [a,s,d])
 * @param jobRecommendOffset useSelectorで取得したoffset一覧（５４個分）
 * @param initJobList Redux_初期表示用求人一覧
 * @param publicationDate 更新日
 */
export interface Props {
  targetName: string;
  categoryConstants: string;
  categoryFlg: string;
  rankingList: (string[] | number[])[];
  bargraphSelected: ReturnType<typeof useBargraphSelected>;
  queryParams: AppQueryParams;
  jobRecommendOffset: JobRecommendOffsetState;
  initJobList: JobTrendRecommendListState;
  publicationDate: string;
  isTrendPageVisited: string;
}

export const JobCountBargraphAreaByCategory: FC<Props> = ({
  targetName,
  categoryConstants,
  categoryFlg,
  rankingList,
  bargraphSelected,
  initJobList,
  queryParams,
  jobRecommendOffset,
  publicationDate,
  isTrendPageVisited,
}) => {
  const masterCache = useAppSelector(masterSelector);

  const initialDisplayFlagRequirements = useAppSelector(
    initialDisplayFlagRequirementsSelector
  );
  const initialDisplayFlagIncome = useAppSelector(
    initialDisplayFlagIncomeSelector
  );
  const initialDisplayFlagWorkplaceEnvironment = useAppSelector(
    initialDisplayFlagWorkplaceEnvironmentSelector
  );
  const initialDisplayFlagHolidayAndWayOfWorking = useAppSelector(
    initialDisplayFlagHolidayAndWayOfWorkingSelector
  );
  const initialDisplayFlagEmployeeAverageAge = useAppSelector(
    initialDisplayFlagEmployeeAverageAgeSelector
  );
  const initialDisplayFlagEmploymentStatus = useAppSelector(
    initialDisplayFlagEmploymentStatusSelector
  );
  const initialDisplayFlagWelfareBenefits = useAppSelector(
    initialDisplayFlagWelfareBenefitsSelector
  );
  const initialDisplayFlagLanguageAndPositions = useAppSelector(
    initialDisplayFlagLanguageAndPositionsSelector
  );
  const initialDisplayFlagEstablish = useAppSelector(
    initialDisplayFlagEstablishSelector
  );

  const requirementsJobListLoading = useAppSelector(
    jobRecommendListForTrendRequirementsLoadingSelector
  );
  const requirementsJobList = useAppSelector(
    jobRecommendListForTrendRequirementsEntitySelector
  );
  const incomeJobListLoading = useAppSelector(
    jobRecommendListForTrendIncomeLoadingSelector
  );
  const incomeJobList = useAppSelector(
    jobRecommendListForTrendIncomeEntitySelector
  );
  const workplaceEnvironmentJobListLoading = useAppSelector(
    jobRecommendListForTrendWorkplaceEnvironmentLoadingSelector
  );
  const workplaceEnvironmentJobList = useAppSelector(
    jobRecommendListForTrendWorkplaceEnvironmentEntitySelector
  );
  const holidayAndWayOfWorkingJobListLoading = useAppSelector(
    jobRecommendListForTrendHolidayAndWayOfWorkingLoadingSelector
  );
  const holidayAndWayOfWorkingJobList = useAppSelector(
    jobRecommendListForTrendHolidayAndWayOfWorkingEntitySelector
  );
  const employeeAverageAgeJobListLoading = useAppSelector(
    jobRecommendListForTrendEmployeeAverageAgeLoadingSelector
  );
  const employeeAverageAgeJobList = useAppSelector(
    jobRecommendListForTrendEmployeeAverageAgeEntitySelector
  );
  const employmentStatusJobListLoading = useAppSelector(
    jobRecommendListForTrendEmploymentStatusLoadingSelector
  );
  const employmentStatusJobList = useAppSelector(
    jobRecommendListForTrendEmploymentStatusEntitySelector
  );
  const welfareBenefitsJobListLoading = useAppSelector(
    jobRecommendListForTrendWelfareBenefitsLoadingSelector
  );
  const welfareBenefitsJobList = useAppSelector(
    jobRecommendListForTrendWelfareBenefitsEntitySelector
  );
  const languageAndPositionsJobListLoading = useAppSelector(
    jobRecommendListForTrendLanguageAndPositionsLoadingSelector
  );
  const languageAndPositionsJobList = useAppSelector(
    jobRecommendListForTrendLanguageAndPositionsEntitySelector
  );
  const establishJobListLoading = useAppSelector(
    jobRecommendListForTrendEstablishLoadingSelector
  );
  const establishJobList = useAppSelector(
    jobRecommendListForTrendEstablishEntitySelector
  );
  const useV2JobSearchListApi = useFeatureFlag(
    FeatureFlagSettings.JOB_SEARCH_LIST_PC
  );

  let hydrogenOp;
  let judgeEmploymentStatus = false;
  let judgeEmployeeAverageAge = false;
  if (queryParams.op) {
    hydrogenOp = queryParams.op ? createOpParams(queryParams.op) : undefined;
  }
  if (hydrogenOp !== undefined) {
    if (hydrogenOp.employmentStatus !== undefined) {
      judgeEmploymentStatus = true;
    }
    if (hydrogenOp.employeeAverageAge !== undefined) {
      judgeEmployeeAverageAge = true;
    }
  }
  // 求人一覧(jobsearchlist)へ遷移用URLのop変換
  let requirementsOp;
  let workplaceEnvironmentOp;
  let holidayAndWayOfWorkingOp;
  let employeeAverageAgeOp;
  let employmentStatusOp;
  let welfareBenefitsOp;
  let positionsAndLanguageSkillsOp;

  if (bargraphSelected.requirementsSelected) {
    requirementsOp = generateOpValues({
      requirementsList: [
        bargraphSelected.requirementsSelected.value as RequirementsRequestCode,
      ],
    });
  }
  if (bargraphSelected.workplaceEnvironmentSelected) {
    workplaceEnvironmentOp = generateOpValues({
      workplaceEnvironmentList: [
        bargraphSelected.workplaceEnvironmentSelected
          .value as WorkplaceEnvironmentRequestCode,
      ],
    });
  }
  if (bargraphSelected.holidayAndWayOfWorkingSelected) {
    holidayAndWayOfWorkingOp = generateOpValues({
      holidayAndWayOfWorkingList: [
        bargraphSelected.holidayAndWayOfWorkingSelected
          .value as HolidayAndWayOfWorkingRequestCode,
      ],
    });
  }
  if (bargraphSelected.employeeAverageAgeSelected) {
    employeeAverageAgeOp = generateOpValues({
      employeeAverageAgeList: [
        bargraphSelected.employeeAverageAgeSelected
          .value as EmployeeAverageAgeRequestCode,
      ],
    });
  }
  if (bargraphSelected.employmentStatusSelected) {
    if (bargraphSelected.employmentStatusSelected.value === '3') {
      employmentStatusOp = ['19'];
    } else {
      employmentStatusOp = generateOpValues({
        employmentStatusList: [
          bargraphSelected.employmentStatusSelected
            .value as EmploymentStatusRequestCode,
        ],
      });
    }
  }
  if (bargraphSelected.welfareBenefitsSelected) {
    welfareBenefitsOp = generateOpValues({
      welfareBenefitsList: [
        bargraphSelected.welfareBenefitsSelected
          .value as WelfareBenefitsRequestCode,
      ],
    });
  }
  if (
    bargraphSelected.positionsSelected?.value ||
    bargraphSelected.languageSkillsSelected?.value
  ) {
    positionsAndLanguageSkillsOp = generateOpValues(
      bargraphSelected.positionsSelected?.value
        ? {
            positionsList: [
              bargraphSelected.positionsSelected.value as PositionsRequestCode,
            ],
          }
        : {
            languageSkillsList: [
              bargraphSelected.languageSkillsSelected
                ?.value as LanguageSkillsRequestCode,
            ],
          }
    );
  }

  // 項目ごとにpreBtnの設定とopの設定
  const appQueryParams = createPreBtn(queryParams);
  const appQueryParamsRequire = { ...appQueryParams };
  if (appQueryParamsRequire.op) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: op の型は `Item` なのに代入してる値の型は `(string | Item)[]` なので要修正
    appQueryParamsRequire.op = [appQueryParamsRequire.op, requirementsOp].sort(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TODO: a,b の型が `string[] | undefined` で - 演算ができないので要修正
      (a, b) => a - b
    );
  } else {
    appQueryParamsRequire.op = requirementsOp;
  }

  const appQueryParamsIncome = { ...appQueryParams };
  appQueryParamsIncome.ha = [
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: lowerId は undefined になる可能性があるので要修正
    bargraphSelected.incomeSelected?.lowerId,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: upperId は undefined になる可能性があるので要修正
    bargraphSelected.incomeSelected?.upperId,
  ];

  const appQueryParamsWork = { ...appQueryParams };
  if (appQueryParamsWork.op) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: op の型は `Item` なのに代入してる値の型は `(string | Item)[]` なので要修正
    appQueryParamsWork.op = [
      appQueryParamsWork.op,
      workplaceEnvironmentOp,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TODO: a,b の型が `string[] | undefined` で - 演算ができないので要修正
    ].sort((a, b) => a - b);
  } else {
    appQueryParamsWork.op = workplaceEnvironmentOp;
  }

  const appQueryParamsHoliday = { ...appQueryParams };
  if (appQueryParamsHoliday.op) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: op の型は `Item` なのに代入してる値の型は `(string | Item)[]` なので要修正
    appQueryParamsHoliday.op = [
      appQueryParamsHoliday.op,
      holidayAndWayOfWorkingOp,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TODO: a,b の型が `string[] | undefined` で - 演算ができないので要修正
    ].sort((a, b) => a - b);
  } else {
    appQueryParamsHoliday.op = holidayAndWayOfWorkingOp;
  }

  const appQueryParamsStatus = { ...appQueryParams };
  if (appQueryParamsStatus.op) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: op の型は `Item` なのに代入してる値の型は `(string | Item)[]` なので要修正
    appQueryParamsStatus.op = [
      appQueryParamsStatus.op,
      employmentStatusOp,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TODO: a,b の型が `string[] | undefined` で - 演算ができないので要修正
    ].sort((a, b) => a - b);
    if (judgeEmploymentStatus) {
      appQueryParamsStatus.op = employmentStatusOp;
    }
  } else {
    appQueryParamsStatus.op = employmentStatusOp;
  }

  const appQueryParamsBenefit = { ...appQueryParams };
  if (appQueryParamsBenefit.op) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: op の型は `Item` なのに代入してる値の型は `(string | Item)[]` なので要修正
    appQueryParamsBenefit.op = [
      appQueryParamsBenefit.op,
      welfareBenefitsOp,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TODO: a,b の型が `string[] | undefined` で - 演算ができないので要修正
    ].sort((a, b) => a - b);
  } else {
    appQueryParamsBenefit.op = welfareBenefitsOp;
  }

  const appQueryParamsAvgAge = { ...appQueryParams };
  if (appQueryParamsAvgAge.op) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: op の型は `Item` なのに代入してる値の型は `(string | Item)[]` なので要修正
    appQueryParamsAvgAge.op = [
      appQueryParamsAvgAge.op,
      employeeAverageAgeOp,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TODO: a,b の型が `string[] | undefined` で - 演算ができないので要修正
    ].sort((a, b) => a - b);
    if (judgeEmployeeAverageAge) {
      appQueryParamsAvgAge.op = employeeAverageAgeOp;
    }
  } else {
    appQueryParamsAvgAge.op = employeeAverageAgeOp;
  }

  const appQueryParamsPositionAndLang = { ...appQueryParams };
  if (appQueryParamsPositionAndLang.op) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: op の型は `Item` なのに代入してる値の型は `(string | Item)[]` なので要修正
    appQueryParamsPositionAndLang.op = [
      appQueryParamsPositionAndLang.op,
      positionsAndLanguageSkillsOp,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore TODO: a,b の型が `string[] | undefined` で - 演算ができないので要修正
    ].sort((a, b) => a - b);
  } else {
    appQueryParamsPositionAndLang.op = positionsAndLanguageSkillsOp;
  }

  const appQueryParamsEstablish = { ...appQueryParams };
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore TODO: es の型は `Item | undefined` なのに代入してる値の型は `string | undefined` なので要修正
  appQueryParamsEstablish.es = bargraphSelected.establishSelected?.value;

  const dispatch = useAppDispatch();

  if (categoryFlg === LABEL_FLAG.REQUIREMENT) {
    const analyticsSelectedValue = `${ANALYTICS_OP_NAME}${requirementsOp}`;
    const analyticsValue = `${convertQuery(queryParams)}_${analyticsSelectedValue}`;
    const handleClickCard = () => {
      dispatch(
        measureJobSearchListLink({
          analyticsValue,
        })
      );
    };
    return (
      <div className={styles.bargraphArea}>
        <h2 id="index-requirements" className={styles.bargraphArea__title}>
          {categoryConstants}に関する求人数
        </h2>
        <BargraphLeadText
          targetName={targetName}
          jobData={rankingList[0].map(Number)}
          jobLabel={rankingList[1].map(String)}
          flg={categoryFlg}
        />
        <div className={styles.bargraphArea__publicationDate}>
          更新日：{formatDate(publicationDate)}
        </div>
        <div className={styles.bargraphArea__bargraph}>
          {isTrendPageVisited === '-1' ? (
            <Snackbar
              color="dark_gray"
              freeze
              className={styles.bargraphArea__snackbar}
            >
              <img
                className="bargraphArea__snackbar--img"
                src={CURSOR_IMG_LINK}
                alt="cursor"
                width={24}
                height={24}
              />
              <div>
                <p className="bargraphArea__snackbar--text">
                  グラフをクリックすると、
                </p>
                <p className="bargraphArea__snackbar--text">
                  下の求人が切り替わります。
                </p>
              </div>
              <img
                className="bargraphArea__snackbar--imgclose"
                src={CLOSE_IMG_LINK}
                alt="close"
                width={20}
                height={20}
              />
            </Snackbar>
          ) : (
            <></>
          )}
          <BargraphForJobTrend
            jobData={rankingList[0].map(Number)}
            joblabel={rankingList[1].map(String)}
            setSelected={bargraphSelected.setRequirementsSelected}
            setSubSelected={null}
            masterCache={masterCache}
            queryParamsForTrend={queryParams}
            jobRecommendOffset={jobRecommendOffset}
            bargraphSelectedName={bargraphSelected.requirementsSelected.text}
            userAgent="PC"
            useV2JobSearchListApi={useV2JobSearchListApi}
          />
        </div>

        <div className={styles.bargraphArea__annotation}>
          ※グラフをクリックすると、下の求人が切り替わります。
        </div>
        <div className="bargraphArea__jobRecommend--title">
          <p className="bargraphArea__jobRecommend--titleSide" />
          <h3 className="bargraphArea__jobRecommend--titleText">
            {targetName}×{bargraphSelected.requirementsSelected.text} の求人
          </h3>
        </div>
        {(() => {
          if (initialDisplayFlagRequirements === -1) {
            return (
              <InitialJobRecommend
                targetName={targetName}
                jobList={
                  initJobList.requirements ? initJobList.requirements : {}
                }
                flag={LABEL_FLAG.REQUIREMENT}
                initialUrl={makeStaticUrl(
                  appQueryParamsRequire,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                analyticsSelectedValue={analyticsSelectedValue}
                selectedCount={bargraphSelected.requirementsSelected.count}
              />
            );
          }
          if (
            requirementsJobListLoading === 'pending' ||
            requirementsJobListLoading === 'error'
          ) {
            return (
              <div className="bargraphArea__jobRecommend--loading">
                <img
                  src="/cmn_web/img/index/loading.gif"
                  alt="loading"
                  width="40px"
                  height="40px"
                />
              </div>
            );
          }
          return (
            <JobRecommend
              jobList={requirementsJobList}
              analyticsSelectedValue={analyticsSelectedValue}
              selectedCount={bargraphSelected.requirementsSelected.count}
            />
          );
        })()}

        {bargraphSelected.requirementsSelected.count !== 0 ? (
          <a
            className="bargraphArea__jobRecommend--something"
            href={makeStaticUrl(
              appQueryParamsRequire,
              DODA_SITE_URL.jobSearchList
            ).replace(/,/g, '%2C')}
            onClick={handleClickCard}
            rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
          >
            <div>
              {targetName}×{bargraphSelected.requirementsSelected.text}{' '}
              すべての求人を見る
            </div>
            <button type="button">
              <ArrowRight size="M" color="blue" />
            </button>
          </a>
        ) : (
          <div className="bargraphArea__jobRecommend--zero">
            <div>
              {targetName}×{bargraphSelected.requirementsSelected.text}{' '}
              すべての求人を見る
            </div>
            <div>
              <button type="button">
                <ArrowRight size="M" color="gray" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (categoryFlg === LABEL_FLAG.INCOME) {
    const analyticsSelectedValue = `${ANALYTICS_HA_NAME}${bargraphSelected.incomeSelected?.lowerId}%2C${bargraphSelected.incomeSelected?.upperId}`;
    const analyticsValue = `${convertQuery(queryParams)}_${analyticsSelectedValue}`;
    const handleClickCard = () => {
      dispatch(
        measureJobSearchListLink({
          analyticsValue,
        })
      );
    };
    return (
      <div className={styles.bargraphArea}>
        <h2 id="index-income" className={styles.bargraphArea__title}>
          {categoryConstants}別の求人数
        </h2>
        <BargraphLeadText
          targetName={targetName}
          jobData={rankingList[0].map(Number)}
          jobLabel={rankingList[1].map(String)}
          flg={categoryFlg}
        />
        <div className={styles.bargraphArea__publicationDate}>
          更新日：{formatDate(publicationDate)}
        </div>
        <div className={styles.bargraphArea__bargraph}>
          {isTrendPageVisited === '-1' ? (
            <Snackbar
              color="dark_gray"
              freeze
              className={styles.bargraphArea__snackbar}
            >
              <img
                className="bargraphArea__snackbar--img"
                src={CURSOR_IMG_LINK}
                alt="cursor"
                width={24}
                height={24}
              />
              <div>
                <p className="bargraphArea__snackbar--text">
                  グラフをクリックすると、
                </p>
                <p className="bargraphArea__snackbar--text">
                  下の求人が切り替わります。
                </p>
              </div>
              <img
                className="bargraphArea__snackbar--imgclose"
                src={CLOSE_IMG_LINK}
                alt="close"
                width={20}
                height={20}
              />
            </Snackbar>
          ) : (
            <></>
          )}
          <BargraphForJobTrend
            jobData={rankingList[0].map(Number)}
            joblabel={rankingList[1].map(String)}
            setSelected={bargraphSelected.setIncomeSelected}
            setSubSelected={null}
            masterCache={masterCache}
            queryParamsForTrend={queryParams}
            jobRecommendOffset={jobRecommendOffset}
            bargraphSelectedName={bargraphSelected.incomeSelected?.name}
            userAgent="PC"
            useV2JobSearchListApi={useV2JobSearchListApi}
          />
        </div>
        <div className={styles.bargraphArea__annotation}>
          ※グラフをクリックすると、下の求人が切り替わります。
        </div>
        <div className="bargraphArea__jobRecommend--title">
          <p className="bargraphArea__jobRecommend--titleSide" />
          {queryParams.ha ? (
            <h3 className="bargraphArea__jobRecommend--titleText">
              {bargraphSelected.incomeSelected?.name} の求人
            </h3>
          ) : (
            <h3 className="bargraphArea__jobRecommend--titleText">
              {targetName}×{bargraphSelected.incomeSelected?.name} の求人
            </h3>
          )}
        </div>
        {(() => {
          if (initialDisplayFlagIncome === -1) {
            return (
              <InitialJobRecommend
                targetName={targetName}
                jobList={initJobList.income ? initJobList.income : {}}
                flag={LABEL_FLAG.INCOME}
                initialUrl={makeStaticUrl(
                  appQueryParamsIncome,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                analyticsSelectedValue={analyticsSelectedValue}
                selectedCount={bargraphSelected.incomeSelected.count}
              />
            );
          }
          if (
            incomeJobListLoading === 'pending' ||
            incomeJobListLoading === 'error'
          ) {
            return (
              <div className="bargraphArea__jobRecommend--loading">
                <img
                  src="/cmn_web/img/index/loading.gif"
                  alt="loading"
                  width="40px"
                  height="40px"
                />
              </div>
            );
          }
          return (
            <JobRecommend
              jobList={incomeJobList}
              analyticsSelectedValue={analyticsSelectedValue}
              selectedCount={bargraphSelected.incomeSelected.count}
            />
          );
        })()}

        {(() => {
          if (bargraphSelected.incomeSelected.count !== 0) {
            const { ha } = appQueryParamsIncome;
            let jobSerchListLinkText;
            if (queryParams.ha) {
              jobSerchListLinkText = `${bargraphSelected.incomeSelected?.name} すべての求人を見る`;
            } else {
              jobSerchListLinkText = `${targetName}×${bargraphSelected.incomeSelected?.name} すべての求人を見る`;
            }
            return (
              <a
                className="bargraphArea__jobRecommend--something"
                href={makeStaticUrl(
                  appQueryParamsIncome,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                onClick={handleClickCard}
                rel={
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore TODO: ha が undefined の可能性を考慮した処理に修正
                  !(ha[0] === '100' && ha[1] === '0') ||
                  queryParams.ne ||
                  queryParams.es
                    ? 'nofollow'
                    : ''
                }
              >
                <div>{jobSerchListLinkText}</div>
                <button type="button">
                  <ArrowRight size="M" color="blue" />
                </button>
              </a>
            );
          }
          if (queryParams.ha) {
            return (
              <div className="bargraphArea__jobRecommend--zero">
                <div>
                  {bargraphSelected.incomeSelected?.name} すべての求人を見る
                </div>
                <div>
                  <button type="button">
                    <ArrowRight size="M" color="gray" />
                  </button>
                </div>
              </div>
            );
          }
          return (
            <div className="bargraphArea__jobRecommend--zero">
              <div>
                {targetName}×{bargraphSelected.incomeSelected?.name}{' '}
                すべての求人を見る
              </div>
              <div>
                <button type="button">
                  <ArrowRight size="M" color="gray" />
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }
  if (categoryFlg === LABEL_FLAG.WORKPLACE_ENVIRONMENT) {
    const analyticsSelectedValue = `${ANALYTICS_OP_NAME}${workplaceEnvironmentOp}`;
    const analyticsValue = `${convertQuery(queryParams)}_${analyticsSelectedValue}`;
    const handleClickCard = () => {
      dispatch(
        measureJobSearchListLink({
          analyticsValue,
        })
      );
    };
    return (
      <div className={styles.bargraphArea}>
        <h2
          id="index-workplace-environment"
          className={styles.bargraphArea__title}
        >
          {categoryConstants}に関する求人数
        </h2>
        <BargraphLeadText
          targetName={targetName}
          jobData={rankingList[0].map(Number)}
          jobLabel={rankingList[1].map(String)}
          flg={categoryFlg}
        />
        <div className={styles.bargraphArea__publicationDate}>
          更新日：{formatDate(publicationDate)}
        </div>
        <div className={styles.bargraphArea__bargraph}>
          {isTrendPageVisited === '-1' ? (
            <Snackbar
              color="dark_gray"
              freeze
              className={styles.bargraphArea__snackbar}
            >
              <img
                className="bargraphArea__snackbar--img"
                src={CURSOR_IMG_LINK}
                alt="cursor"
                width={24}
                height={24}
              />
              <div>
                <p className="bargraphArea__snackbar--text">
                  グラフをクリックすると、
                </p>
                <p className="bargraphArea__snackbar--text">
                  下の求人が切り替わります。
                </p>
              </div>
              <img
                className="bargraphArea__snackbar--imgclose"
                src={CLOSE_IMG_LINK}
                alt="close"
                width={20}
                height={20}
              />
            </Snackbar>
          ) : (
            <></>
          )}
          <BargraphForJobTrend
            jobData={rankingList[0].map(Number)}
            joblabel={rankingList[1].map(String)}
            setSelected={bargraphSelected.setWorkplaceEnvironmentSelected}
            setSubSelected={null}
            masterCache={masterCache}
            queryParamsForTrend={queryParams}
            jobRecommendOffset={jobRecommendOffset}
            bargraphSelectedName={
              bargraphSelected?.workplaceEnvironmentSelected.text
            }
            userAgent="PC"
            useV2JobSearchListApi={useV2JobSearchListApi}
          />
        </div>
        <div className={styles.bargraphArea__annotation}>
          ※グラフをクリックすると、下の求人が切り替わります。
        </div>
        <div className="bargraphArea__jobRecommend--title">
          <p className="bargraphArea__jobRecommend--titleSide" />
          <h3 className="bargraphArea__jobRecommend--titleText">
            {targetName}×{bargraphSelected?.workplaceEnvironmentSelected.text}
            の求人
          </h3>
        </div>
        {(() => {
          if (initialDisplayFlagWorkplaceEnvironment === -1) {
            return (
              <InitialJobRecommend
                targetName={targetName}
                jobList={
                  initJobList.workplaceEnvironment
                    ? initJobList.workplaceEnvironment
                    : {}
                }
                flag={LABEL_FLAG.WORKPLACE_ENVIRONMENT}
                initialUrl={makeStaticUrl(
                  appQueryParamsWork,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                analyticsSelectedValue={analyticsSelectedValue}
                selectedCount={
                  bargraphSelected.workplaceEnvironmentSelected.count
                }
              />
            );
          }
          if (
            workplaceEnvironmentJobListLoading === 'pending' ||
            workplaceEnvironmentJobListLoading === 'error'
          ) {
            return (
              <div className="bargraphArea__jobRecommend--loading">
                <img
                  src="/cmn_web/img/index/loading.gif"
                  alt="loading"
                  width="40px"
                  height="40px"
                />
              </div>
            );
          }
          return (
            <JobRecommend
              jobList={workplaceEnvironmentJobList}
              analyticsSelectedValue={analyticsSelectedValue}
              selectedCount={
                bargraphSelected.workplaceEnvironmentSelected.count
              }
            />
          );
        })()}

        {bargraphSelected.workplaceEnvironmentSelected.count !== 0 ? (
          <a
            className="bargraphArea__jobRecommend--something"
            href={makeStaticUrl(
              appQueryParamsWork,
              DODA_SITE_URL.jobSearchList
            ).replace(/,/g, '%2C')}
            onClick={handleClickCard}
            rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
          >
            <div>
              {targetName}×{bargraphSelected.workplaceEnvironmentSelected.text}{' '}
              すべての求人を見る
            </div>
            <button type="button">
              <ArrowRight size="M" color="blue" />
            </button>
          </a>
        ) : (
          <div className="bargraphArea__jobRecommend--zero">
            <div>
              {targetName}×{bargraphSelected.workplaceEnvironmentSelected.text}{' '}
              すべての求人を見る
            </div>
            <div>
              <button type="button">
                <ArrowRight size="M" color="gray" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (categoryFlg === LABEL_FLAG.HOLIDAY_AND_WAY_OF_WORKING) {
    const analyticsSelectedValue = `${ANALYTICS_OP_NAME}${holidayAndWayOfWorkingOp}`;
    const analyticsValue = `${convertQuery(queryParams)}_${analyticsSelectedValue}`;
    const handleClickCard = () => {
      dispatch(
        measureJobSearchListLink({
          analyticsValue,
        })
      );
    };
    return (
      <div className={styles.bargraphArea}>
        <h2
          id="index-holiday-and-way-of-working"
          className={styles.bargraphArea__title}
        >
          {categoryConstants}に関する求人数
        </h2>
        <BargraphLeadText
          targetName={targetName}
          jobData={rankingList[0].map(Number)}
          jobLabel={rankingList[1].map(String)}
          flg={categoryFlg}
        />
        <div className={styles.bargraphArea__publicationDate}>
          更新日：{formatDate(publicationDate)}
        </div>
        <div className={styles.bargraphArea__bargraph}>
          {isTrendPageVisited === '-1' ? (
            <Snackbar
              color="dark_gray"
              freeze
              className={styles.bargraphArea__snackbar}
            >
              <img
                className="bargraphArea__snackbar--img"
                src={CURSOR_IMG_LINK}
                alt="cursor"
                width={24}
                height={24}
              />
              <div>
                <p className="bargraphArea__snackbar--text">
                  グラフをクリックすると、
                </p>
                <p className="bargraphArea__snackbar--text">
                  下の求人が切り替わります。
                </p>
              </div>
              <img
                className="bargraphArea__snackbar--imgclose"
                src={CLOSE_IMG_LINK}
                alt="close"
                width={20}
                height={20}
              />
            </Snackbar>
          ) : (
            <></>
          )}
          <BargraphForJobTrend
            jobData={rankingList[0].map(Number)}
            joblabel={rankingList[1].map(String)}
            setSelected={bargraphSelected.setHolidayAndWayOfWorkingSelected}
            setSubSelected={null}
            masterCache={masterCache}
            queryParamsForTrend={queryParams}
            jobRecommendOffset={jobRecommendOffset}
            bargraphSelectedName={
              bargraphSelected.holidayAndWayOfWorkingSelected.text
            }
            userAgent="PC"
            useV2JobSearchListApi={useV2JobSearchListApi}
          />
        </div>
        <div className={styles.bargraphArea__annotation}>
          ※グラフをクリックすると、下の求人が切り替わります。
        </div>
        <div className="bargraphArea__jobRecommend--title">
          <p className="bargraphArea__jobRecommend--titleSide" />
          <h3 className="bargraphArea__jobRecommend--titleText">
            {targetName}×{bargraphSelected.holidayAndWayOfWorkingSelected.text}
            の求人
          </h3>
        </div>
        {(() => {
          if (initialDisplayFlagHolidayAndWayOfWorking === -1) {
            return (
              <InitialJobRecommend
                targetName={targetName}
                jobList={
                  initJobList.holidayAndWayOfWorking
                    ? initJobList.holidayAndWayOfWorking
                    : {}
                }
                flag={LABEL_FLAG.HOLIDAY_AND_WAY_OF_WORKING}
                initialUrl={makeStaticUrl(
                  appQueryParamsHoliday,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                analyticsSelectedValue={analyticsSelectedValue}
                selectedCount={
                  bargraphSelected.holidayAndWayOfWorkingSelected.count
                }
              />
            );
          }
          if (
            holidayAndWayOfWorkingJobListLoading === 'pending' ||
            holidayAndWayOfWorkingJobListLoading === 'error'
          ) {
            return (
              <div className="bargraphArea__jobRecommend--loading">
                <img
                  src="/cmn_web/img/index/loading.gif"
                  alt="loading"
                  width="40px"
                  height="40px"
                />
              </div>
            );
          }
          return (
            <JobRecommend
              jobList={holidayAndWayOfWorkingJobList}
              analyticsSelectedValue={analyticsSelectedValue}
              selectedCount={
                bargraphSelected.holidayAndWayOfWorkingSelected.count
              }
            />
          );
        })()}

        {bargraphSelected.holidayAndWayOfWorkingSelected.count !== 0 ? (
          <a
            className="bargraphArea__jobRecommend--something"
            href={makeStaticUrl(
              appQueryParamsHoliday,
              DODA_SITE_URL.jobSearchList
            ).replace(/,/g, '%2C')}
            onClick={handleClickCard}
            rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
          >
            <div>
              {targetName}×
              {bargraphSelected.holidayAndWayOfWorkingSelected.text}{' '}
              すべての求人を見る
            </div>
            <button type="button">
              <ArrowRight size="M" color="blue" />
            </button>
          </a>
        ) : (
          <div className="bargraphArea__jobRecommend--zero">
            <div>
              {targetName}×
              {bargraphSelected.holidayAndWayOfWorkingSelected.text}{' '}
              すべての求人を見る
            </div>
            <div>
              <button type="button">
                <ArrowRight size="M" color="gray" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (categoryFlg === LABEL_FLAG.EMPLOYEE_AVERAGE_AGE) {
    const analyticsSelectedValue = `${ANALYTICS_OP_NAME}${employeeAverageAgeOp}`;
    const analyticsValue = `${convertQuery(queryParams)}_${analyticsSelectedValue}`;
    const handleClickCard = () => {
      dispatch(
        measureJobSearchListLink({
          analyticsValue,
        })
      );
    };
    return (
      <div className={styles.bargraphArea}>
        <h2
          id="index-employee-average-age"
          className={styles.bargraphArea__title}
        >
          {categoryConstants}別の求人数
        </h2>
        <BargraphLeadText
          targetName={targetName}
          jobData={rankingList[0].map(Number)}
          jobLabel={rankingList[1].map(String)}
          flg={categoryFlg}
        />
        <div className={styles.bargraphArea__publicationDate}>
          更新日：{formatDate(publicationDate)}
        </div>
        <div className={styles.bargraphArea__bargraph}>
          {isTrendPageVisited === '-1' ? (
            <Snackbar
              color="dark_gray"
              freeze
              className={styles.bargraphArea__snackbar}
            >
              <img
                className="bargraphArea__snackbar--img"
                src={CURSOR_IMG_LINK}
                alt="cursor"
                width={24}
                height={24}
              />
              <div>
                <p className="bargraphArea__snackbar--text">
                  グラフをクリックすると、
                </p>
                <p className="bargraphArea__snackbar--text">
                  下の求人が切り替わります。
                </p>
              </div>
              <img
                className="bargraphArea__snackbar--imgclose"
                src={CLOSE_IMG_LINK}
                alt="close"
                width={20}
                height={20}
              />
            </Snackbar>
          ) : (
            <></>
          )}
          <BargraphForJobTrend
            jobData={rankingList[0].map(Number)}
            joblabel={rankingList[1].map(String)}
            setSelected={bargraphSelected.setEmployeeAverageAgeSelected}
            setSubSelected={null}
            masterCache={masterCache}
            queryParamsForTrend={queryParams}
            jobRecommendOffset={jobRecommendOffset}
            bargraphSelectedName={
              bargraphSelected.employeeAverageAgeSelected?.text
            }
            userAgent="PC"
            useV2JobSearchListApi={useV2JobSearchListApi}
          />
        </div>

        <div className={styles.bargraphArea__annotation}>
          ※グラフをクリックすると、下の求人が切り替わります。
        </div>
        <div className="bargraphArea__jobRecommend--title">
          <p className="bargraphArea__jobRecommend--titleSide" />
          {judgeEmployeeAverageAge ? (
            <h3 className="bargraphArea__jobRecommend--titleText">
              社員の平均年齢{bargraphSelected.employeeAverageAgeSelected?.text}
              の求人
            </h3>
          ) : (
            <h3 className="bargraphArea__jobRecommend--titleText">
              {targetName}×社員の平均年齢
              {bargraphSelected.employeeAverageAgeSelected?.text} の求人
            </h3>
          )}
        </div>
        {(() => {
          if (initialDisplayFlagEmployeeAverageAge === -1) {
            return (
              <InitialJobRecommend
                targetName={targetName}
                jobList={
                  initJobList.employeeAverageAge
                    ? initJobList.employeeAverageAge
                    : {}
                }
                flag={LABEL_FLAG.EMPLOYEE_AVERAGE_AGE}
                initialUrl={makeStaticUrl(
                  appQueryParamsAvgAge,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                analyticsSelectedValue={analyticsSelectedValue}
                selectedCount={
                  bargraphSelected.employeeAverageAgeSelected.count
                }
              />
            );
          }
          if (
            employeeAverageAgeJobListLoading === 'pending' ||
            employeeAverageAgeJobListLoading === 'error'
          ) {
            return (
              <div className="bargraphArea__jobRecommend--loading">
                <img
                  src="/cmn_web/img/index/loading.gif"
                  alt="loading"
                  width="40px"
                  height="40px"
                />
              </div>
            );
          }
          return (
            <JobRecommend
              jobList={employeeAverageAgeJobList}
              analyticsSelectedValue={analyticsSelectedValue}
              selectedCount={bargraphSelected.employeeAverageAgeSelected.count}
            />
          );
        })()}

        {(() => {
          if (bargraphSelected.employeeAverageAgeSelected.count !== 0) {
            if (judgeEmployeeAverageAge) {
              return (
                <a
                  className="bargraphArea__jobRecommend--something"
                  href={makeStaticUrl(
                    appQueryParamsAvgAge,
                    DODA_SITE_URL.jobSearchList
                  ).replace(/,/g, '%2C')}
                  onClick={handleClickCard}
                  rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
                >
                  <div>
                    社員の平均年齢
                    {bargraphSelected.employeeAverageAgeSelected?.text}{' '}
                    すべての求人を見る
                  </div>
                  <button type="button">
                    <ArrowRight size="M" color="blue" />
                  </button>
                </a>
              );
            }
            return (
              <a
                className="bargraphArea__jobRecommend--something"
                href={makeStaticUrl(
                  appQueryParamsAvgAge,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                onClick={handleClickCard}
                rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
              >
                <div>
                  {targetName}×社員の平均年齢
                  {bargraphSelected.employeeAverageAgeSelected?.text}{' '}
                  すべての求人を見る
                </div>
                <button type="button">
                  <ArrowRight size="M" color="blue" />
                </button>
              </a>
            );
          }
          if (judgeEmployeeAverageAge) {
            return (
              <div className="bargraphArea__jobRecommend--zero">
                <div>
                  社員の平均年齢
                  {bargraphSelected.employeeAverageAgeSelected?.text}{' '}
                  すべての求人を見る
                </div>
                <div>
                  <button type="button">
                    <ArrowRight size="M" color="gray" />
                  </button>
                </div>
              </div>
            );
          }
          return (
            <div className="bargraphArea__jobRecommend--zero">
              <div>
                {targetName}×社員の平均年齢
                {bargraphSelected.employeeAverageAgeSelected?.text}{' '}
                すべての求人を見る
              </div>
              <div>
                <button type="button">
                  <ArrowRight size="M" color="gray" />
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }
  if (categoryFlg === LABEL_FLAG.EMPLOYMMENT_STATUS) {
    const analyticsSelectedValue = `${ANALYTICS_OP_NAME}${employmentStatusOp}`;
    const analyticsValue = `${convertQuery(queryParams)}_${analyticsSelectedValue}`;
    const handleClickCard = () => {
      dispatch(
        measureJobSearchListLink({
          analyticsValue,
        })
      );
    };
    return (
      <div className={styles.bargraphArea}>
        <h2
          id="index-employmment-status"
          className={styles.bargraphArea__title}
        >
          {categoryConstants}別の求人数
        </h2>
        <BargraphLeadText
          targetName={targetName}
          jobData={rankingList[0].map(Number)}
          jobLabel={rankingList[1].map(String)}
          flg={categoryFlg}
        />
        <div className={styles.bargraphArea__publicationDate}>
          更新日：{formatDate(publicationDate)}
        </div>
        <div className={styles.bargraphArea__bargraph}>
          {isTrendPageVisited === '-1' ? (
            <Snackbar
              color="dark_gray"
              freeze
              className={styles.bargraphArea__snackbar}
            >
              <img
                className="bargraphArea__snackbar--img"
                src={CURSOR_IMG_LINK}
                alt="cursor"
                width={24}
                height={24}
              />
              <div>
                <p className="bargraphArea__snackbar--text">
                  グラフをクリックすると、
                </p>
                <p className="bargraphArea__snackbar--text">
                  下の求人が切り替わります。
                </p>
              </div>
              <img
                className="bargraphArea__snackbar--imgclose"
                src={CLOSE_IMG_LINK}
                alt="close"
                width={20}
                height={20}
              />
            </Snackbar>
          ) : (
            <></>
          )}
          <BargraphForJobTrend
            jobData={rankingList[0].map(Number)}
            joblabel={rankingList[1].map(String)}
            setSelected={bargraphSelected.setEmploymentStatusSelected}
            setSubSelected={null}
            masterCache={masterCache}
            queryParamsForTrend={queryParams}
            jobRecommendOffset={jobRecommendOffset}
            bargraphSelectedName={
              bargraphSelected.employmentStatusSelected?.text
            }
            userAgent="PC"
            useV2JobSearchListApi={useV2JobSearchListApi}
          />
        </div>

        <div className={styles.bargraphArea__annotation}>
          ※グラフをクリックすると、下の求人が切り替わります。
        </div>
        <div className="bargraphArea__jobRecommend--title">
          <p className="bargraphArea__jobRecommend--titleSide" />
          {judgeEmploymentStatus ? (
            <h3 className="bargraphArea__jobRecommend--titleText">
              {bargraphSelected.employmentStatusSelected?.text} の求人
            </h3>
          ) : (
            <h3 className="bargraphArea__jobRecommend--titleText">
              {targetName}×{bargraphSelected.employmentStatusSelected?.text}{' '}
              の求人
            </h3>
          )}
        </div>
        {(() => {
          if (initialDisplayFlagEmploymentStatus === -1) {
            return (
              <InitialJobRecommend
                targetName={targetName}
                jobList={
                  initJobList.employmentStatus
                    ? initJobList.employmentStatus
                    : {}
                }
                flag={LABEL_FLAG.EMPLOYMMENT_STATUS}
                initialUrl={makeStaticUrl(
                  appQueryParamsStatus,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                analyticsSelectedValue={analyticsSelectedValue}
                selectedCount={bargraphSelected.employmentStatusSelected.count}
              />
            );
          }
          if (
            employmentStatusJobListLoading === 'pending' ||
            employmentStatusJobListLoading === 'error'
          ) {
            return (
              <div className="bargraphArea__jobRecommend--loading">
                <img
                  src="/cmn_web/img/index/loading.gif"
                  alt="loading"
                  width="40px"
                  height="40px"
                />
              </div>
            );
          }
          return (
            <JobRecommend
              jobList={employmentStatusJobList}
              analyticsSelectedValue={analyticsSelectedValue}
              selectedCount={bargraphSelected.employmentStatusSelected.count}
            />
          );
        })()}

        {(() => {
          if (bargraphSelected.employmentStatusSelected?.count !== 0) {
            if (judgeEmploymentStatus) {
              return (
                <a
                  className="bargraphArea__jobRecommend--something"
                  href={makeStaticUrl(
                    appQueryParamsStatus,
                    DODA_SITE_URL.jobSearchList
                  ).replace(/,/g, '%2C')}
                  onClick={handleClickCard}
                  rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
                >
                  <div>
                    {bargraphSelected.employmentStatusSelected?.text}{' '}
                    すべての求人を見る
                  </div>
                  <button type="button">
                    <ArrowRight size="M" color="blue" />
                  </button>
                </a>
              );
            }
            return (
              <a
                className="bargraphArea__jobRecommend--something"
                href={makeStaticUrl(
                  appQueryParamsStatus,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                onClick={handleClickCard}
                rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
              >
                <div>
                  {targetName}×{bargraphSelected.employmentStatusSelected?.text}{' '}
                  すべての求人を見る
                </div>
                <button type="button">
                  <ArrowRight size="M" color="blue" />
                </button>
              </a>
            );
          }
          if (judgeEmploymentStatus) {
            return (
              <div className="bargraphArea__jobRecommend--zero">
                <div>
                  {bargraphSelected.employmentStatusSelected?.text}{' '}
                  すべての求人を見る
                </div>
                <div>
                  <button type="button">
                    <ArrowRight size="M" color="gray" />
                  </button>
                </div>
              </div>
            );
          }
          return (
            <div className="bargraphArea__jobRecommend--zero">
              <div>
                {targetName}×{bargraphSelected.employmentStatusSelected?.text}{' '}
                すべての求人を見る
              </div>
              <div>
                <button type="button">
                  <ArrowRight size="M" color="gray" />
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }
  if (categoryFlg === LABEL_FLAG.WELFARE_BENEFITS) {
    const analyticsSelectedValue = `${ANALYTICS_OP_NAME}${welfareBenefitsOp}`;
    const analyticsValue = `${convertQuery(queryParams)}_${analyticsSelectedValue}`;
    const handleClickCard = () => {
      dispatch(
        measureJobSearchListLink({
          analyticsValue,
        })
      );
    };
    return (
      <div className={styles.bargraphArea}>
        <h2 id="index-welfare-benefits" className={styles.bargraphArea__title}>
          {categoryConstants}に関する求人数
        </h2>
        <BargraphLeadText
          targetName={targetName}
          jobData={rankingList[0].map(Number)}
          jobLabel={rankingList[1].map(String)}
          flg={categoryFlg}
        />
        <div className={styles.bargraphArea__publicationDate}>
          更新日：{formatDate(publicationDate)}
        </div>
        <div className={styles.bargraphArea__bargraph}>
          {isTrendPageVisited === '-1' ? (
            <Snackbar
              color="dark_gray"
              freeze
              className={styles.bargraphArea__snackbar}
            >
              <img
                className="bargraphArea__snackbar--img"
                src={CURSOR_IMG_LINK}
                alt="cursor"
                width={24}
                height={24}
              />
              <div>
                <p className="bargraphArea__snackbar--text">
                  グラフをクリックすると、
                </p>
                <p className="bargraphArea__snackbar--text">
                  下の求人が切り替わります。
                </p>
              </div>
              <img
                className="bargraphArea__snackbar--imgclose"
                src={CLOSE_IMG_LINK}
                alt="close"
                width={20}
                height={20}
              />
            </Snackbar>
          ) : (
            <></>
          )}
          <BargraphForJobTrend
            jobData={rankingList[0].map(Number)}
            joblabel={rankingList[1].map(String)}
            setSelected={bargraphSelected.setWelfareBenefitsSelected}
            setSubSelected={null}
            masterCache={masterCache}
            queryParamsForTrend={queryParams}
            jobRecommendOffset={jobRecommendOffset}
            bargraphSelectedName={
              bargraphSelected?.welfareBenefitsSelected.text
            }
            userAgent="PC"
            useV2JobSearchListApi={useV2JobSearchListApi}
          />
        </div>

        <div className={styles.bargraphArea__annotation}>
          ※グラフをクリックすると、下の求人が切り替わります。
        </div>
        <div className="bargraphArea__jobRecommend--title">
          <p className="bargraphArea__jobRecommend--titleSide" />
          <h3 className="bargraphArea__jobRecommend--titleText">
            {targetName}×{bargraphSelected?.welfareBenefitsSelected.text} の求人
          </h3>
        </div>
        {(() => {
          if (initialDisplayFlagWelfareBenefits === -1) {
            return (
              <InitialJobRecommend
                targetName={targetName}
                jobList={
                  initJobList.welfareBenefits ? initJobList.welfareBenefits : {}
                }
                flag={LABEL_FLAG.WELFARE_BENEFITS}
                initialUrl={makeStaticUrl(
                  appQueryParamsBenefit,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                analyticsSelectedValue={analyticsSelectedValue}
                selectedCount={bargraphSelected.welfareBenefitsSelected.count}
              />
            );
          }
          if (
            welfareBenefitsJobListLoading === 'pending' ||
            welfareBenefitsJobListLoading === 'error'
          ) {
            return (
              <div className="bargraphArea__jobRecommend--loading">
                <img
                  src="/cmn_web/img/index/loading.gif"
                  alt="loading"
                  width="40px"
                  height="40px"
                />
              </div>
            );
          }
          return (
            <JobRecommend
              jobList={welfareBenefitsJobList}
              analyticsSelectedValue={analyticsSelectedValue}
              selectedCount={bargraphSelected.welfareBenefitsSelected.count}
            />
          );
        })()}

        {bargraphSelected.welfareBenefitsSelected?.count !== 0 ? (
          <a
            className="bargraphArea__jobRecommend--something"
            href={makeStaticUrl(
              appQueryParamsBenefit,
              DODA_SITE_URL.jobSearchList
            ).replace(/,/g, '%2C')}
            onClick={handleClickCard}
            rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
          >
            <div>
              {targetName}×{bargraphSelected?.welfareBenefitsSelected.text}{' '}
              すべての求人を見る
            </div>
            <button type="button">
              <ArrowRight size="M" color="blue" />
            </button>
          </a>
        ) : (
          <div className="bargraphArea__jobRecommend--zero">
            <div>
              {targetName}×{bargraphSelected?.welfareBenefitsSelected.text}{' '}
              すべての求人を見る
            </div>
            <div>
              <button type="button">
                <ArrowRight size="M" color="gray" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (categoryFlg === LABEL_FLAG.LANGUAGE_AND_POSITIONS) {
    const analyticsSelectedValue = `${ANALYTICS_OP_NAME}${positionsAndLanguageSkillsOp}`;
    const analyticsValue = `${convertQuery(queryParams)}_${analyticsSelectedValue}`;
    const handleClickCard = () => {
      dispatch(
        measureJobSearchListLink({
          analyticsValue,
        })
      );
    };
    return (
      <div className={styles.bargraphArea}>
        <h2
          id="index-language-and-positions"
          className={styles.bargraphArea__title}
        >
          {categoryConstants}に関する求人数
        </h2>
        <BargraphLeadText
          targetName={targetName}
          jobData={rankingList[0].map(Number)}
          jobLabel={rankingList[1].map(String)}
          flg={categoryFlg}
        />
        <div className={styles.bargraphArea__publicationDate}>
          更新日：{formatDate(publicationDate)}
        </div>
        <div className={styles.bargraphArea__bargraph}>
          {isTrendPageVisited === '-1' ? (
            <Snackbar
              color="dark_gray"
              freeze
              className={styles.bargraphArea__snackbar}
            >
              <img
                className="bargraphArea__snackbar--img"
                src={CURSOR_IMG_LINK}
                alt="cursor"
                width={24}
                height={24}
              />
              <div>
                <p className="bargraphArea__snackbar--text">
                  グラフをクリックすると、
                </p>
                <p className="bargraphArea__snackbar--text">
                  下の求人が切り替わります。
                </p>
              </div>
              <img
                className="bargraphArea__snackbar--imgclose"
                src={CLOSE_IMG_LINK}
                alt="close"
                width={20}
                height={20}
              />
            </Snackbar>
          ) : (
            <></>
          )}
          <BargraphForJobTrend
            jobData={rankingList[0].map(Number)}
            joblabel={rankingList[1].map(String)}
            setSelected={bargraphSelected.setLanguageSkillsSelected}
            setSubSelected={bargraphSelected.setPositionsSelected}
            masterCache={masterCache}
            queryParamsForTrend={queryParams}
            jobRecommendOffset={jobRecommendOffset}
            bargraphSelectedName={
              bargraphSelected.positionsSelected?.text
                ? bargraphSelected.positionsSelected?.text
                : bargraphSelected.languageSkillsSelected?.text
            }
            userAgent="PC"
            useV2JobSearchListApi={useV2JobSearchListApi}
          />
        </div>

        <div className={styles.bargraphArea__annotation}>
          ※グラフをクリックすると、下の求人が切り替わります。
        </div>
        <div className="bargraphArea__jobRecommend--title">
          <p className="bargraphArea__jobRecommend--titleSide" />
          <h3 className="bargraphArea__jobRecommend--titleText">
            {targetName}×
            {bargraphSelected.positionsSelected?.text
              ? bargraphSelected.positionsSelected?.text
              : bargraphSelected.languageSkillsSelected?.text}
            の求人
          </h3>
        </div>
        {(() => {
          if (initialDisplayFlagLanguageAndPositions === -1) {
            return (
              <InitialJobRecommend
                targetName={targetName}
                jobList={
                  initJobList.positionsAndLanguageSkills
                    ? initJobList.positionsAndLanguageSkills
                    : {}
                }
                flag={LABEL_FLAG.LANGUAGE_AND_POSITIONS}
                initialUrl={makeStaticUrl(
                  appQueryParamsPositionAndLang,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                analyticsSelectedValue={analyticsSelectedValue}
                selectedCount={
                  bargraphSelected.positionsSelected
                    ? bargraphSelected.positionsSelected?.count
                    : bargraphSelected.languageSkillsSelected?.count
                }
              />
            );
          }
          if (
            languageAndPositionsJobListLoading === 'pending' ||
            languageAndPositionsJobListLoading === 'error'
          ) {
            return (
              <div className="bargraphArea__jobRecommend--loading">
                <img
                  src="/cmn_web/img/index/loading.gif"
                  alt="loading"
                  width="40px"
                  height="40px"
                />
              </div>
            );
          }
          return (
            <JobRecommend
              jobList={languageAndPositionsJobList}
              analyticsSelectedValue={analyticsSelectedValue}
              selectedCount={
                bargraphSelected.positionsSelected
                  ? bargraphSelected.positionsSelected?.count
                  : bargraphSelected.languageSkillsSelected?.count
              }
            />
          );
        })()}
        {(bargraphSelected.positionsSelected &&
          bargraphSelected.positionsSelected?.count !== 0) ||
        (bargraphSelected.languageSkillsSelected &&
          bargraphSelected.languageSkillsSelected?.count !== 0) ? (
          <a
            className="bargraphArea__jobRecommend--something"
            href={makeStaticUrl(
              appQueryParamsPositionAndLang,
              DODA_SITE_URL.jobSearchList
            ).replace(/,/g, '%2C')}
            onClick={handleClickCard}
            rel={queryParams.ne || queryParams.es ? 'nofollow' : ''}
          >
            <div>
              {targetName}×
              {bargraphSelected.positionsSelected?.text
                ? bargraphSelected.positionsSelected?.text
                : bargraphSelected.languageSkillsSelected?.text}{' '}
              すべての求人を見る
            </div>
            <button type="button">
              <ArrowRight size="M" color="blue" />
            </button>
          </a>
        ) : (
          <div className="bargraphArea__jobRecommend--zero">
            <div>
              {targetName}×
              {bargraphSelected.positionsSelected?.text
                ? bargraphSelected.positionsSelected?.text
                : bargraphSelected.languageSkillsSelected?.text}{' '}
              すべての求人を見る
            </div>
            <div>
              <button type="button">
                <ArrowRight size="M" color="gray" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (categoryFlg === LABEL_FLAG.ESTABLISH) {
    const analyticsSelectedValue = `${ANALYTICS_ES_NAME}${bargraphSelected.establishSelected?.value}`;
    const analyticsValue = `${convertQuery(queryParams)}_${analyticsSelectedValue}`;
    const handleClickCard = () => {
      dispatch(
        measureJobSearchListLink({
          analyticsValue,
        })
      );
    };
    return (
      <div className={styles.bargraphArea}>
        <h2 id="index-establish" className={styles.bargraphArea__title}>
          {categoryConstants}別の求人数
        </h2>
        <BargraphLeadText
          targetName={targetName}
          jobData={rankingList[0].map(Number)}
          jobLabel={rankingList[1].map(String)}
          flg={categoryFlg}
        />
        <div className={styles.bargraphArea__publicationDate}>
          更新日：{formatDate(publicationDate)}
        </div>
        <div className={styles.bargraphArea__bargraph}>
          {isTrendPageVisited === '-1' ? (
            <Snackbar
              color="dark_gray"
              freeze
              className={styles.bargraphArea__snackbar}
            >
              <img
                className="bargraphArea__snackbar--img"
                src={CURSOR_IMG_LINK}
                alt="cursor"
                width={24}
                height={24}
              />
              <div>
                <p className="bargraphArea__snackbar--text">
                  グラフをクリックすると、
                </p>
                <p className="bargraphArea__snackbar--text">
                  下の求人が切り替わります。
                </p>
              </div>
              <img
                className="bargraphArea__snackbar--imgclose"
                src={CLOSE_IMG_LINK}
                alt="close"
                width={20}
                height={20}
              />
            </Snackbar>
          ) : (
            <></>
          )}
          <BargraphForJobTrend
            jobData={rankingList[0].map(Number)}
            joblabel={rankingList[1].map(String)}
            setSelected={bargraphSelected.setEstablishSelected}
            setSubSelected={null}
            masterCache={masterCache}
            queryParamsForTrend={queryParams}
            jobRecommendOffset={jobRecommendOffset}
            bargraphSelectedName={bargraphSelected.establishSelected?.text}
            userAgent="PC"
            useV2JobSearchListApi={useV2JobSearchListApi}
          />
        </div>
        <div className={styles.bargraphArea__annotation}>
          ※グラフをクリックすると、下の求人が切り替わります。
        </div>
        <div className="bargraphArea__jobRecommend--title">
          <p className="bargraphArea__jobRecommend--titleSide" />
          {queryParams.es ? (
            <h3 className="bargraphArea__jobRecommend--titleText">
              設立{bargraphSelected.establishSelected?.text} の求人
            </h3>
          ) : (
            <h3 className="bargraphArea__jobRecommend--titleText">
              {targetName}×設立{bargraphSelected.establishSelected?.text} の求人
            </h3>
          )}
        </div>
        {(() => {
          if (initialDisplayFlagEstablish === -1) {
            return (
              <InitialJobRecommend
                targetName={targetName}
                jobList={initJobList.establish ? initJobList.establish : {}}
                flag={LABEL_FLAG.ESTABLISH}
                initialUrl={makeStaticUrl(
                  appQueryParamsEstablish,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                analyticsSelectedValue={analyticsSelectedValue}
                selectedCount={bargraphSelected.establishSelected.count}
              />
            );
          }
          if (
            establishJobListLoading === 'pending' ||
            establishJobListLoading === 'error'
          ) {
            return (
              <div className="bargraphArea__jobRecommend--loading">
                <img
                  src="/cmn_web/img/index/loading.gif"
                  alt="loading"
                  width="40px"
                  height="40px"
                />
              </div>
            );
          }
          return (
            <JobRecommend
              jobList={establishJobList}
              analyticsSelectedValue={analyticsSelectedValue}
              selectedCount={bargraphSelected.establishSelected.count}
            />
          );
        })()}

        {(() => {
          if (bargraphSelected.establishSelected.count !== 0) {
            if (queryParams.es) {
              return (
                <a
                  className="bargraphArea__jobRecommend--something"
                  href={makeStaticUrl(
                    appQueryParamsEstablish,
                    DODA_SITE_URL.jobSearchList
                  ).replace(/,/g, '%2C')}
                  onClick={handleClickCard}
                  rel={
                    queryParams.ne || queryParams.op || queryParams.ha
                      ? 'nofollow'
                      : ''
                  }
                >
                  <div>
                    設立{bargraphSelected.establishSelected?.text}{' '}
                    すべての求人を見る
                  </div>
                  <button type="button">
                    <ArrowRight size="M" color="blue" />
                  </button>
                </a>
              );
            }
            return (
              <a
                className="bargraphArea__jobRecommend--something"
                href={makeStaticUrl(
                  appQueryParamsEstablish,
                  DODA_SITE_URL.jobSearchList
                ).replace(/,/g, '%2C')}
                onClick={handleClickCard}
                rel={
                  queryParams.ne || queryParams.op || queryParams.ha
                    ? 'nofollow'
                    : ''
                }
              >
                <div>
                  {targetName}×設立{bargraphSelected.establishSelected?.text}{' '}
                  すべての求人を見る
                </div>
                <button type="button">
                  <ArrowRight size="M" color="blue" />
                </button>
              </a>
            );
          }
          if (queryParams.es) {
            return (
              <div className="bargraphArea__jobRecommend--zero">
                <div>
                  設立{bargraphSelected.establishSelected?.text}{' '}
                  すべての求人を見る
                </div>
                <div>
                  <button type="button">
                    <ArrowRight size="M" color="gray" />
                  </button>
                </div>
              </div>
            );
          }
          return (
            <div className="bargraphArea__jobRecommend--zero">
              <div>
                {targetName}×設立{bargraphSelected.establishSelected?.text}{' '}
                すべての求人を見る
              </div>
              <div>
                <button type="button">
                  <ArrowRight size="M" color="gray" />
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }
  return <></>;
};
